import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import { PAYMENTS_QUERY } from "./queries";
import { Variables, PaymentRow } from "./types";
import { PaymentMethod } from "../types";
import { PaymentNode } from "./queries";
import Table, {
  TableControls,
  TableFooter,
  type Row,
  buildActionColumn,
  buildExpandableCountMultiColumn
} from "@/components/Table";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { toFormat } from "@/lib/dateHelpers";
import { renderPaymentStatus, renderPaymentType, sourcesData, paymentStatusData } from "../_utils";
import useCapabilities, { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { useNavigate } from "@/router";
import SearchInput from "@/components/SearchInput";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import usePagination from "@/hooks/usePagination";
import TabLayout from "../_TabLayout";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import MultiSelect from "@/components/MultiSelect";
import { reject, isEmpty, sumBy } from "lodash";
import { Path } from "@/router";
import { useParams } from "@/hooks/useSearchParams";
import { ExpandedPaymentsRow } from "./_components/ExpandedPaymentsRow";
import { openBrowser } from "@/lib/urlUtils";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import NotesTable from "@/features/global/components/NotesTable";
import { NoteNotableValues } from "@/types";
import useAmplitude from "@/hooks/useAmplitude";
import { BILLING_ANALYTICS_EVENTS, getBillingAnalyticsData } from "@/pages/billing/_utils/amplitude";

const mapCommerceOrders = (data: PaymentNode[]) =>
  (data || []).map(({ updatedAt, balance, lineItems, notes, ...payment }) => ({
    id: payment.id,
    updatedAt: formatDate(updatedAt) || "",
    type: payment.type,
    referenceNumber: payment.referenceNumber,
    payer: payment.payer,
    payorName: payment.type === PaymentMethod.FINANCIAL_ADJUSTMENT ? "Financial Adjustment" : payment.payer.name,
    orders: payment.orderPayments.length,
    amount: centsToDollarsFormatted(payment.amountCents),
    refund: centsToDollarsFormatted(payment.refundedAmountCents),
    net: centsToDollarsFormatted(payment.amountCents - payment.refundedAmountCents),
    status: payment.status,
    notes: notes || [],
    orderPayments: payment.orderPayments.map(({ order, ledgerEntries }) => ({
      id: order.id,
      pgaId: order.person.pgaId,
      stage: order.status,
      paymentStatus: order.paymentStatus,
      person: order.person,
      description: order.description,
      lineItems: order.lineItems,
      addons: order.addons,
      netPayment: centsToDollarsFormatted(sumBy(ledgerEntries, "amountCents"))
    }))
  }));

const formatDate = (date?: string): string | undefined => (date && toFormat(date)) || undefined;

export default function PaymentsPage() {
  useRequireCapability({ domain: DOMAIN.COMMERCE, permission: PERMISSION.viewOrders });
  const { trackEvent } = useAmplitude();
  const hasTrackedView = useRef(false);
  const { params, addSearchParam, deleteSearchParams, searchParamsUrl } = useParams();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { Menu, setMenu } = useTableRowMenu();
  const { hasPermission } = useCapabilities();
  const [expanded, setExpanded] = useState<Record<string, string | null>>({});
  const onExpand = useCallback((rowId: string, panelId: string | null) => {
    setExpanded((prev) => ({ ...prev, [rowId]: panelId }));
  }, []);
  const { isCompactLayout } = useIsCompactLayout();
  const [selectedStatusValues, setSelectedStatusValues] = useState<string[]>(params.status || []);
  const [selectedSourceValues, setSelectedSourceValues] = useState<string[]>(params.type || []);

  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "query"
  });

  const [variables, setVariables] = useState<Variables>(params);
  const { data, loading } = useQuery(PAYMENTS_QUERY, {
    variables: { ...variables, query: debouncedSearch || undefined, ...paginationVariables }
  });

  useEffect(() => {
    if (hasTrackedView.current || loading || !data?.commercePayments) {
      return;
    }

    hasTrackedView.current = true;
    trackEvent(
      BILLING_ANALYTICS_EVENTS.paymentsViewed,
      getBillingAnalyticsData({
        source: "payments_tab",
        resultCount: data.commercePayments.nodes?.length
      })
    );
  }, [data, loading, trackEvent]);

  const navigate = useNavigate();
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | undefined>(undefined);

  const pattern = "/billing/orders/payment";

  function onClickRow(row: Row<PaymentRow>, event?: React.MouseEvent) {
    const url = `${pattern}/${row.original.id}?return_to=/billing/payments&${searchParamsUrl}`;

    // Check if user is holding Cmd (Mac) or Ctrl (Windows/Linux) to open in new tab
    if (event && (event.metaKey || event.ctrlKey)) {
      // Construct the full URL for opening in new tab
      const fullUrl = `${window.location.origin}${url}`;
      openBrowser(fullUrl);
    } else {
      // Regular click navigates to payment details page
      navigate(url as Path);
    }
  }

  const paymentData: PaymentRow[] = mapCommerceOrders(data?.commercePayments.nodes || []);

  const expandedFiltersState = {
    sources: selectedSourceValues.length > 0,
    status: selectedStatusValues.length > 0
  };

  const activeFilterCount = Object.values(expandedFiltersState).filter(Boolean).length;

  const handleClearAllFilters = () => {
    clearSearch();
    setSelectedStatusValues([]);
    setSelectedSourceValues([]);
    setVariables({});
    deleteSearchParams(["status[]", "type[]"]);
    resetPagination();
  };

  const renderNotesPanel = useCallback(
    (row: { original: PaymentRow }) => {
      const notes = row.original.notes || [];
      if (notes.length === 0) return null;

      return (
        <NotesTable
          variant="details-panel"
          data={notes}
          title={`Notes (${notes.length})`}
          noteEditing={{
            enabled: hasPermission(PERMISSION.managePayments),
            notableId: row.original.id,
            notableType: NoteNotableValues.PAYMENT,
            refetchQueries: [PAYMENTS_QUERY]
          }}
        />
      );
    },
    [hasPermission]
  );

  return (
    <TabLayout>
      <Menu />
      <div className="w-full">
        <div className="pt-6" />
        <Table
          data={paymentData || []}
          loading={loading}
          onClickRow={onClickRow}
          renderControls={() => (
            <TableControls
              pagination={createHeaderPagination(data?.commercePayments)}
              useExpandedFilters={true}
              expandedFiltersState={expandedFiltersState}
              onClearAllFilters={handleClearAllFilters}
              activeFilters={activeFilterCount}
            >
              <SearchInput
                placeholder="Search Payments"
                query={searchValue}
                onChange={(value) => {
                  setSearchValue(value);
                  resetPagination();
                }}
                onClear={() => {
                  clearSearch();
                  resetPagination();
                }}
              />
              <div data-name="sources" data-filter-size="custom">
                <MultiSelect
                  label="Source"
                  selectedValues={selectedSourceValues}
                  options={sourcesData}
                  onSelect={(value, checked) => {
                    const status = checked
                      ? [...selectedSourceValues, value]
                      : selectedSourceValues.filter((v) => v !== value);
                    setSelectedSourceValues(reject(status, isEmpty));
                    setVariables((prevValue) => ({
                      ...prevValue,
                      type: reject(status, isEmpty)
                    }));
                    addSearchParam("type[]", reject(status, isEmpty).join(","));
                    resetPagination();
                  }}
                  onClear={() => {
                    setSelectedSourceValues([]);
                    setVariables((prevValue) => ({
                      ...prevValue,
                      type: []
                    }));
                    deleteSearchParams("type[]");
                    resetPagination();
                  }}
                  className="w-full"
                />
              </div>
              <div data-name="status" data-filter-size="custom">
                <MultiSelect
                  label="Status"
                  selectedValues={selectedStatusValues}
                  options={paymentStatusData}
                  onSelect={(value, checked) => {
                    const status = checked
                      ? [...selectedStatusValues, value]
                      : selectedStatusValues.filter((v) => v !== value);
                    setSelectedStatusValues(reject(status, isEmpty));
                    setVariables((prevValue) => ({
                      ...prevValue,
                      status: reject(status, isEmpty)
                    }));
                    addSearchParam("status[]", reject(status, isEmpty).join(","));
                    resetPagination();
                  }}
                  onClear={() => {
                    setSelectedStatusValues([]);
                    setVariables((prevValue) => ({
                      ...prevValue,
                      status: []
                    }));
                    deleteSearchParams("status[]");
                    resetPagination();
                  }}
                  className="w-full"
                />
              </div>
            </TableControls>
          )}
          getRowCanExpand={(row) => row.original.orderPayments.length > 0 || (row.original.notes?.length ?? 0) > 0}
          renderExpandedRow={({ row }) => {
            if (isCompactLayout) {
              return (
                <>
                  <ExpandedPaymentsRow row={row} />
                  {renderNotesPanel(row)}
                </>
              );
            }
            switch (expanded[row.id]) {
              case "orders":
                return <ExpandedPaymentsRow row={row} />;
              case "notes":
                return renderNotesPanel(row) ?? <></>;
              default:
                return <></>;
            }
          }}
          columns={[
            {
              header: "Payment Source",
              id: "paymentSource",
              cell: ({ row: { original } }) => {
                return original.type === PaymentMethod.FINANCIAL_ADJUSTMENT ? (
                  original.payer.name
                ) : (
                  <>
                    {renderPaymentType(original.type)}
                    {original.type === PaymentMethod.CHECK ? ` #${original.referenceNumber}` : ""}
                  </>
                );
              },
              size: 250
            },
            {
              accessorKey: "updatedAt",
              header: "Modified",
              enableSorting: false
            },
            { accessorKey: "payorName", header: "Payor", enableSorting: false, size: 250 },
            buildExpandableCountMultiColumn<PaymentRow>({
              panelId: "orders",
              accessorKey: "orders",
              header: "Orders",
              getCount: (row) => row.orderPayments.length,
              expanded,
              onExpand
            }),
            buildExpandableCountMultiColumn<PaymentRow>({
              panelId: "notes",
              accessorKey: "notes",
              header: "Notes",
              getCount: (row) => row.notes?.length ?? 0,
              expanded,
              onExpand
            }),
            {
              accessorKey: "amount",
              header: "Amount",
              enableSorting: false
            },
            {
              accessorKey: "refund",
              header: "Refund",
              enableSorting: false
            },
            {
              accessorKey: "net",
              header: "Net",
              enableSorting: false
            },
            {
              header: "Stage",
              id: "stage",
              cell: ({ row: { original } }) => renderPaymentStatus(original.status),
              size: 100,
              meta: {
                headerClassName: "hide-header-in-column"
              }
            },
            buildActionColumn<PaymentRow>({
              getActions: (row) => [
                {
                  text: "View Payment",
                  onClick: () => {
                    const url = `${pattern}/${row.id}?return_to=/billing/payments&${searchParamsUrl}`;
                    navigate(url as Path);
                  }
                }
              ],
              onViewClick: (row) => {
                setSelectedPgaId(row.orderPayments[0]?.pgaId);
                setIsPersonSideSheetOpen(true);
              },
              viewButtonTitle: "View Person",
              actionId,
              setMenu
            })
          ]}
          hasActiveFilters={searchValue || activeFilterCount > 0}
          renderFooter={() => <TableFooter pagination={createFooterPagination(data?.commercePayments)} />}
        />
        <PersonSideSheet
          open={isPersonSideSheetOpen}
          onClose={() => {
            setIsPersonSideSheetOpen(false);
            setSelectedPgaId(undefined);
          }}
          pgaId={selectedPgaId ?? null}
        />
      </div>
    </TabLayout>
  );
}
